import { useEffect, useMemo, useState } from 'react';
import {
    MantineReactTable,
    useMantineReactTable,
    type MRT_ColumnDef,
} from 'mantine-react-table';
import {
    ActionIcon,
    Box,
    Button,
    Flex,
    Group,
    Select,
    Text,
    Title,
    Tooltip,
} from '@mantine/core';
import {
    IconEdit,
    IconTrash,
    IconPlus,
    IconChevronLeft,
    IconChevronRight,
    IconChevronsLeft,
    IconChevronsRight
} from '@tabler/icons-react';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { UserModal } from '../components/UserModal';
import User from '../users/user';
import { getAllUsers } from '../api/userApi';
import { convertDateTimeToLocalTime } from '../infrastructures/utils';
import { editUserApi, addUserApi, deleteUserApi } from '../api/userApi';
import { toast } from 'react-toastify';
import AddEditUserRequest from '../models/requests/AddEditUserRequest';
import { useAuth } from '../customAuth/authStateHook';

export default function UsersView() {
    const [users, setUsers] = useState<User[]>([]);
    const [isTableLoading, setIsTableLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [showUserModal, setShowUserModal] = useState(false);
    const [editUser, setEditUser] = useState<AddEditUserRequest | null>(null);
    const { isSuperAdmin } = useAuth();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [globalFilter, setGlobalFilter] = useState('');
    const [inProgress, setInProgress] = useState(false);

    // Filter users first, then paginate
    const filteredUsers = users.filter((user) => {
        if (!globalFilter) return true;
        const searchLower = globalFilter.toLowerCase();
        return (
            user.firstName.toLowerCase().includes(searchLower) ||
            user.lastName.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower)
        );
    });

    // Calculate pagination values based on filtered data
    const totalItems = filteredUsers.length;
    const totalPages = Math.ceil(totalItems / pageSize) || 1;
    const from = totalItems > 0 ? (page - 1) * pageSize + 1 : 0;
    const to = Math.min(page * pageSize, totalItems);

    // Get paginated data from filtered results
    const paginatedUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize);

    const fetchUsers = async () => {
        const users = await getAllUsers();
        setUsers(users);
        setIsTableLoading(false);
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Reset to page 1 when pageSize changes and we exceed available pages
    useEffect(() => {
        if (page > totalPages && totalPages > 0) {
            setPage(1);
        }
    }, [page, totalPages]);

    const handleDelete = async () => {
        const response = await deleteUserApi({ userId: selectedUserId });
        if (response.success) {
            setUsers(users.filter((u) => u.userId !== selectedUserId));
            setShowDeleteModal(false);
            toast.success('User deleted successfully');
            setSelectedUserId(null);
        } else {
            toast.error('Something went wrong while deleting the user. Please try again or contact support.');
        }
    };

    const buildAddEditUserRequest = (values: any) => {
        return {
            id: values.id,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            password: values.password,
            isSuperAdmin: values.isSuperAdmin,
            canAccessInternalTools: values.canAccessInternalTools,
        };
    };

    const handleEditUser = async (values: any) => {
        setInProgress(true);
        const data = buildAddEditUserRequest(values);
        const response = await editUserApi(data);

        if (response.success) {
            setUsers(users.map((u) =>
                u.userId === values.id ? { ...u, ...values } : u
            ));
            setShowUserModal(false);
            setEditUser(null);
            toast.success('User updated successfully');
        } else {
            toast.error('Something went wrong while updating the user. Please try again or contact support.');
        }
        setInProgress(false);
    };

    const handleAddUser = async (values: any) => {
        const data = buildAddEditUserRequest(values);
        const response = await addUserApi(data);

        if (response.success && response.data) {
            let newUser = response.data as User;
            setUsers([...users, newUser]);
            setShowUserModal(false);
            toast.success('User added successfully');
            setEditUser(null);
        } else {
            toast.error('Something went wrong while adding the user. Please try again or contact support.');
        }
    };


    const columns = useMemo<MRT_ColumnDef<User>[]>(() => [
        { accessorKey: 'firstName', header: 'First Name' },
        { accessorKey: 'lastName', header: 'Last Name' },
        { accessorKey: 'email', header: 'Email' },
        // {
        //     accessorKey: 'canAccessInternalTools',
        //     header: 'Internal Tools',
        //     Cell: ({ row }) => <Text>{row.original.canAccessInternalTools ? 'Enabled' : 'Disabled'}</Text>,
        // },
        {
            accessorKey: 'dateCreatedUtc',
            header: 'Date Created',
            accessorFn: (row) => convertDateTimeToLocalTime(row.dateCreatedUtc),
        },
    ], []);

    const table = useMantineReactTable({
        columns,
        data: paginatedUsers,
        manualPagination: true,
        pageCount: totalPages,
        state: {
            isLoading: isTableLoading,
            globalFilter,
        },
        onGlobalFilterChange: (filter) => {
            setGlobalFilter(filter || '');
            setPage(1); // Reset to first page when searching
        },
        enableRowActions: isSuperAdmin,
        positionActionsColumn: 'last',
        enableExpandAll: false,
        renderBottomToolbarCustomActions: () => (
            <Flex justify="space-between" align="center" gap="sm" p="sm" bg="var(--color-grey-mauve)" wrap="wrap" w="100%" className="users-pagination">
                <Flex align="center" gap="sm" wrap="wrap">
                    <Text size="sm">Rows per page</Text>

                    <Select
                        value={pageSize.toString()}
                        onChange={(value) => {
                            setPageSize(Number(value));
                            setPage(1);
                        }}
                        radius="xs"
                        data={['5', '10', '20', '50', '100']}
                        w={80}
                        size="xs"
                    />

                    <Text size="sm">
                        {from}-{to} of {totalItems}{globalFilter && filteredUsers.length !== users.length ? ` (filtered from ${users.length})` : ''}
                    </Text>
                </Flex>

                <Flex align="center" gap="xs">
                    <Button
                        variant="default"
                        size="xs"
                        radius="xs"
                        disabled={page === 1}
                        onClick={() => setPage(1)}
                        className="pagination-button"
                    >
                        <IconChevronsLeft size={16} />
                    </Button>

                    <Button
                        variant="default"
                        size="xs"
                        radius="xs"
                        disabled={page === 1}
                        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                        className="pagination-button"
                    >
                        <IconChevronLeft size={16} />
                    </Button>

                    <Button
                        variant="default"
                        size="xs"
                        radius="xs"
                        disabled={page === totalPages}
                        onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                        className="pagination-button"
                    >
                        <IconChevronRight size={16} />
                    </Button>

                    <Button
                        variant="default"
                        size="xs"
                        radius="xs"
                        disabled={page === totalPages}
                        onClick={() => setPage(totalPages)}
                        className="pagination-button"
                    >
                        <IconChevronsRight size={16} />
                    </Button>
                </Flex>
            </Flex>
        ),
        renderRowActions: ({ row }) => (
            <Group gap="xs" wrap="nowrap">
                <Tooltip label="Edit">
                    <ActionIcon
                        variant="light"
                        color="#8a6a4a"
                        onClick={() => {
                            setEditUser({
                                id: row.original.userId,
                                email: row.original.email,
                                firstName: row.original.firstName,
                                lastName: row.original.lastName,
                                isSuperAdmin: row.original.isSuperAdmin,
                                canAccessInternalTools: row.original.canAccessInternalTools,
                            });
                            setShowUserModal(true);
                        }}
                    >
                        <IconEdit size={18} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Delete">
                    <ActionIcon
                        variant="light"
                        color="#8a6a4a"
                        onClick={() => {
                            setSelectedUserId(row.original.userId);
                            setShowDeleteModal(true);
                        }}
                    >
                        <IconTrash size={18} />
                    </ActionIcon>
                </Tooltip>
            </Group>
        ),

        enableGlobalFilter: true,
        enableColumnFilters: false,
        enableSorting: true,
        enablePagination: false,

        enableColumnActions: false,
        enableColumnOrdering: false,
        enableFullScreenToggle: true,

        renderTopToolbarCustomActions: ({ table }) => (
            <Flex justify="flex-end" w="100%">
                {table.getState().density !== undefined &&
                    table.options.renderToolbarInternalActions?.({ table })}
            </Flex>
        ),

        mantineSearchTextInputProps: {
            placeholder: 'Search users...',
            style: { maxWidth: '300px' },
        },

        mantinePaperProps: {
            className: 'mindvault-table-paper',
        },

        mantineTableContainerProps: {
            style: { overflowX: 'auto' },
        },

        mantineTableProps: {
            highlightOnHover: true,
            withColumnBorders: false,
            verticalSpacing: 'xs',
            style: { minWidth: '920px' },
        },
    });


    return (
        <Box p="xl" className="users-container">
            <Flex mb="lg" align="center" justify="space-between">
                <Box>
                    <Title fw={700} order={2} className="page-title">
                        Users
                    </Title>
                    <Text size="sm" c="dimmed">{totalItems} users</Text>
                </Box>
                {isSuperAdmin && (
                    <Button
                        color="#151514"
                        leftSection={<IconPlus size={16} />}
                        onClick={() => {
                            setEditUser(null);
                            setShowUserModal(true);
                        }}
                    >
                        Add User
                    </Button>
                )}
            </Flex>

            <MantineReactTable table={table} />

            <UserModal
                opened={showUserModal}
                onClose={() => {
                    setShowUserModal(false);
                    setEditUser(null);
                }}
                isSuperAdmin={isSuperAdmin}
                initialValues={editUser}
                onSubmit={async (newUser) => {
                    if (editUser) {
                        await handleEditUser(newUser);
                    } else {
                        await handleAddUser(newUser);
                    }
                }}
                isLoading={inProgress}
            />

            <DeleteConfirmationModal
                dialogVisible={showDeleteModal}
                dialogHandlers={{
                    close: () => {
                        setShowDeleteModal(false);
                        setSelectedUserId(null);
                    }
                }}
                onConfirm={handleDelete}
                message="Are you sure you want to delete this user?"
            />
        </Box>
    );
}

